import { create } from 'zustand';
import { fetchRecords, queryRecords, createRecord, updateRecord, deleteRecord } from '../api/client';
import { TABLES } from '../api/tables';
import { FIELDS } from "../api/fields";

const FRE = FIELDS.rcEvents;
const FRC = FIELDS.rcCheckins;
const FRP = FIELDS.rcPersonnel;

function mapRCEvent(r: Record<string, unknown>): RCEvent {
  return {
    id: r.record_id as number,
    incidentId: (r[FRE.incidentId] as number) || null,
    eventName: (r[FRE.eventName] as string) || "",
    eventType: (r[FRE.eventType] as string) || "",
    eventStatus: (r[FRE.eventStatus] as string) || "open",
    location: (r[FRE.location] as string) || "",
    startTime: (r[FRE.startTime] as string) || "",
    endTime: (r[FRE.endTime] as string) || "",
    checkInCode: (r[FRE.checkInCode] as string) || "",
    notes: (r[FRE.notes] as string) || "",
  };
}
function mapRCCheckin(r: Record<string, unknown>): RCCheckin {
  return {
    id: r.record_id as number,
    eventId: (r[FRC.eventId] as number) || 0,
    personnelId: (r[FRC.personnelId] as string) || "",
    personName: (r[FRC.personName] as string) || "",
    agency: (r[FRC.agency] as string) || "",
    checkInTime: (r[FRC.checkInTime] as string) || "",
    checkOutTime: (r[FRC.checkOutTime] as string) || "",
    method: (r[FRC.method] as string) || "manual",
    notes: (r[FRC.notes] as string) || "",
  };
}
function mapRCPerson(r: Record<string, unknown>): RCPerson {
  return {
    id: r.record_id as number,
    fullName: (r[FRP.fullName] as string) || "",
    agency: (r[FRP.agency] as string) || "",
    position: (r[FRP.position] as string) || "",
    phone: (r[FRP.phone] as string) || "",
    email: (r[FRP.email] as string) || "",
    badgeId: (r[FRP.badgeId] as string) || "",
    pin: (r[FRP.pin] as string) || "",
    status: (r[FRP.status] as string) || "active",
  };
}

export interface RCEvent {
  id: number;
  incidentId: number | null;
  eventName: string;
  eventType: string;
  eventStatus: string;
  location: string;
  startTime: string;
  endTime: string;
  checkInCode: string;
  notes: string;
  [k: string]: unknown;
}
export interface RCCheckin {
  id: number;
  eventId: number;
  personnelId: string;
  personName: string;
  agency: string;
  checkInTime: string;
  checkOutTime: string;
  method: string;
  notes: string;
  [k: string]: unknown;
}
export interface RCPerson {
  id: number;
  fullName: string;
  agency: string;
  position: string;
  phone: string;
  email: string;
  badgeId: string;
  pin: string;
  status: string;
  [k: string]: unknown;
}

interface RollCallState {
  events: RCEvent[];
  checkins: RCCheckin[];
  personnel: RCPerson[];
  loading: boolean;
  fetchEvents: () => Promise<void>;
  fetchCheckins: (eventId?: number) => Promise<void>;
  fetchPersonnel: () => Promise<void>;
  fetchAll: () => Promise<void>;
  createEvent: (data: Record<string, unknown>) => Promise<number | null>;
  updateEvent: (id: number, data: Record<string, unknown>) => Promise<void>;
  closeEvent: (id: number) => Promise<void>;
  checkIn: (data: Record<string, unknown>) => Promise<number | null>;
  checkOut: (id: number) => Promise<void>;
  bulkCheckOut: (eventId: string, by: string) => Promise<number>;
  createPerson: (data: Record<string, unknown>) => Promise<number | null>;
  updatePerson: (id: number, data: Record<string, unknown>) => Promise<void>;
  deletePerson: (id: number) => Promise<void>;
  findByPin: (pin: string) => RCPerson | undefined;
  findByBadgeId: (badgeId: string) => RCPerson | undefined;
}

export const useRollCallStore = create<RollCallState>((set, get) => ({
  events: [],
  checkins: [],
  personnel: [],
  loading: false,

  fetchEvents: async () => {
    try {
      const r = await fetchRecords<Record<string, unknown>>(TABLES.rcEvents, { limit: 500, sort: { field: 'created_at', direction: 'desc' } });
      set({ events: r.records.map(mapRCEvent) });
    } catch (e) { console.error('[RC]', e); }
  },

  fetchCheckins: async (eventId) => {
    try {
      const opts = eventId
        ? { filters: [{ field: FRC.eventId, operator: 'eq' as const, value: eventId }] }
        : {};
      const r = await queryRecords<Record<string, unknown>>(TABLES.rcCheckins, { limit: 2000, ...opts });
      set({ checkins: r.records.map(mapRCCheckin) });
    } catch (e) { console.error('[RC]', e); }
  },

  fetchPersonnel: async () => {
    try {
      const r = await fetchRecords<Record<string, unknown>>(TABLES.rcPersonnel, { limit: 500 });
      set({ personnel: r.records.map(mapRCPerson) });
    } catch (e) { console.error('[RC]', e); }
  },

  fetchAll: async () => {
    set({ loading: true });
    await Promise.all([get().fetchEvents(), get().fetchCheckins(), get().fetchPersonnel()]);
    set({ loading: false });
  },

  createEvent: async (data) => {
    const p: Record<string, unknown> = {};
    if (data.eventName) p[FRE.eventName] = data.eventName;
    if (data.eventType) p[FRE.eventType] = data.eventType;
    p[FRE.eventStatus] = data.eventStatus || 'open';
    if (data.location) p[FRE.location] = data.location;
    if (data.incidentId) p[FRE.incidentId] = data.incidentId;
    if (data.startTime) p[FRE.startTime] = data.startTime;
    if (data.endTime) p[FRE.endTime] = data.endTime;
    if (data.checkInCode) p[FRE.checkInCode] = data.checkInCode;
    if (data.notes) p[FRE.notes] = data.notes;
    try {
      const r = await createRecord<{ record_id: number }>(TABLES.rcEvents, p);
      await get().fetchEvents();
      return r.record_id ?? (r as any).id ?? null;
    } catch (e) { console.error('[RC]', e); return null; }
  },

  updateEvent: async (id, data) => {
    const p: Record<string, unknown> = {};
    if (data.eventName !== undefined) p[FRE.eventName] = data.eventName;
    if (data.eventType !== undefined) p[FRE.eventType] = data.eventType;
    if (data.eventStatus !== undefined) p[FRE.eventStatus] = data.eventStatus;
    if (data.location !== undefined) p[FRE.location] = data.location;
    if (data.startTime !== undefined) p[FRE.startTime] = data.startTime;
    if (data.endTime !== undefined) p[FRE.endTime] = data.endTime;
    if (data.notes !== undefined) p[FRE.notes] = data.notes;
    try {
      await updateRecord(TABLES.rcEvents, id, p);
      await get().fetchEvents();
    } catch (e) { console.error('[RC]', e); }
  },

  closeEvent: async (id) => {
    try {
      await updateRecord(TABLES.rcEvents, id, { [FRE.eventStatus]: 'closed' });
      await get().fetchEvents();
    } catch (e) { console.error('[RC]', e); }
  },

  checkIn: async (data) => {
    const p: Record<string, unknown> = { [FRC.checkInTime]: new Date().toISOString() };
    if (data.eventId) p[FRC.eventId] = data.eventId;
    if (data.personnelId) p[FRC.personnelId] = data.personnelId;
    if (data.personName) p[FRC.personName] = data.personName;
    if (data.agency) p[FRC.agency] = data.agency;
    if (data.method) p[FRC.method] = data.method;
    if (data.notes) p[FRC.notes] = data.notes;
    try {
      const r = await createRecord<{ record_id: number }>(TABLES.rcCheckins, p);
      await get().fetchCheckins();
      return r.record_id ?? (r as any).id ?? null;
    } catch (e) { console.error('[RC]', e); return null; }
  },

  checkOut: async (id) => {
    try {
      await updateRecord(TABLES.rcCheckins, id, { [FRC.checkOutTime]: new Date().toISOString() });
      await get().fetchCheckins();
    } catch (e) { console.error('[RC]', e); }
  },

  bulkCheckOut: async (eventId, _by) => {
    const { checkins } = get();
    const toCheckOut = checkins.filter(c => String(c.eventId) === eventId && !c.checkOutTime);
    const now = new Date().toISOString();
    let count = 0;
    for (const c of toCheckOut) {
      try {
        await updateRecord(TABLES.rcCheckins, c.id, { [FRC.checkOutTime]: now });
        count++;
      } catch (e) { console.error('[RC] bulk checkout', e); }
    }
    await get().fetchCheckins();
    return count;
  },

  createPerson: async (data) => {
    const p: Record<string, unknown> = {};
    if (data.fullName) p[FRP.fullName] = data.fullName;
    if (data.agency) p[FRP.agency] = data.agency;
    if (data.position) p[FRP.position] = data.position;
    if (data.phone) p[FRP.phone] = data.phone;
    if (data.email) p[FRP.email] = data.email;
    if (data.badgeId) p[FRP.badgeId] = data.badgeId;
    if (data.pin) p[FRP.pin] = data.pin;
    p[FRP.status] = data.status || 'active';
    try {
      const r = await createRecord<{ record_id: number }>(TABLES.rcPersonnel, p);
      await get().fetchPersonnel();
      return r.record_id ?? (r as any).id ?? null;
    } catch (e) { console.error('[RC]', e); return null; }
  },

  updatePerson: async (id, data) => {
    const p: Record<string, unknown> = {};
    if (data.fullName !== undefined) p[FRP.fullName] = data.fullName;
    if (data.agency !== undefined) p[FRP.agency] = data.agency;
    if (data.position !== undefined) p[FRP.position] = data.position;
    if (data.phone !== undefined) p[FRP.phone] = data.phone;
    if (data.email !== undefined) p[FRP.email] = data.email;
    if (data.badgeId !== undefined) p[FRP.badgeId] = data.badgeId;
    if (data.pin !== undefined) p[FRP.pin] = data.pin;
    if (data.status !== undefined) p[FRP.status] = data.status;
    try {
      await updateRecord(TABLES.rcPersonnel, id, p);
      await get().fetchPersonnel();
    } catch (e) { console.error('[RC]', e); }
  },

  deletePerson: async (id) => {
    try {
      await deleteRecord(TABLES.rcPersonnel, id);
      await get().fetchPersonnel();
    } catch (e) { console.error('[RC]', e); }
  },

  findByPin: (pin) => get().personnel.find(p => p.pin === pin && p.status === 'active'),
  findByBadgeId: (badgeId) => get().personnel.find(p => p.badgeId === badgeId && p.status === 'active'),

  // V1 compat aliases
  loadAll: async () => { await (get() as any).fetchAll(); },
  loadEvents: async () => { await (get() as any).fetchEvents(); },
  loadPersonnel: async () => { await (get() as any).fetchPersonnel(); },
  loadCheckins: async (eventId?: any) => { await (get() as any).fetchCheckins(eventId); },
  createPersonnel: async (data: any) => { await (get() as any).createPerson(data); },
  updatePersonnel: async (id: any, data: any) => { await (get() as any).updatePerson(id, data); },
  deletePersonnel: async (id: any) => { await (get() as any).deletePerson(id); },
  isLoading: false,
}));
